import BaseModel from './BaseModel';
import Staff from './Staff';
import * as AT from '../../components/container/attributes';
import {url} from '@vuelidate/validators';
import Country from './Country';
import DueDate from './DueDate';
import Journal from './Journal';
import CommunicationMode from './CommunicationMode';
import TaxCode from './TaxCode';
import {DataService} from '../../api';
import {useNotificationsStore} from '../../store/pinia/notifications';
import {translate} from '../../plugins/i18n/i18n';

export default class Address extends BaseModel {
  constructor({
    id,
    number,
    designation,
    active = true,
    customer = false,
    supplier = false,
    potentialCustomer = false,
    potentialSupplier = false,
    street = undefined,
    houseNo = undefined,
    boxNo = undefined,
    postcode = undefined,
    city = undefined,
    country = undefined,
    location = undefined,
    memo = undefined,
    salesInvoiceJournal = undefined,
    salesInvoiceTaxCode = undefined,
    salesInvoiceDueDate = undefined,
    saleDiscountPercentage = undefined,
    saleDiscountDays = undefined,
    salesCreditNoteJournal = undefined,
    salesCreditNoteTaxCode = undefined,
    salesCreditNoteDueDate = undefined,
    purchaseInvoiceJournal = undefined,
    purchaseInvoiceTaxCode = undefined,
    purchaseInvoiceDueDate = undefined,
    purchaseDiscountPercentage = undefined,
    purchaseDiscountDays = undefined,
    purchaseCreditNoteJournal = undefined,
    purchaseCreditNoteTaxCode = undefined,
    purchaseCreditNoteDueDate = undefined,
    purchaseCostAccount = undefined,
    salesRevenueAccount = undefined,
    purchaseResponsible = undefined,
    purchaseAutomaticValidation = false,
    calculatedLocation = false,
    customData = undefined,
    communicationModeData = undefined,
    // We also need to add the key how it is stored in the db (we use the db column names)
    no = undefined,
    locale = undefined,
  }) {
    super(id, number || no, designation);
    this.active = active;
    this.customer = customer;
    this.supplier = supplier;
    this.potentialCustomer = potentialCustomer;
    this.potentialSupplier = potentialSupplier;
    this.street = street;
    this.houseNo = houseNo;
    this.boxNo = boxNo;
    this.postcode = postcode;
    this.city = city;
    this.country = country ? new Country(country) : null;
    this.location = location ? location : null;
    this.calculatedLocation = calculatedLocation;
    this.memo = memo;
    this.salesInvoiceJournal = salesInvoiceJournal
      ? new Journal(salesInvoiceJournal)
      : null;
    this.salesInvoiceTaxCode = salesInvoiceTaxCode
      ? new TaxCode(salesInvoiceTaxCode)
      : null;
    this.salesInvoiceDueDate = salesInvoiceDueDate
      ? new DueDate(salesInvoiceDueDate)
      : null;
    this.saleDiscountPercentage = saleDiscountPercentage ?? null;
    this.saleDiscountDays = saleDiscountDays ?? null;
    this.salesCreditNoteJournal = salesCreditNoteJournal
      ? new Journal(salesCreditNoteJournal)
      : null;
    this.salesCreditNoteTaxCode = salesCreditNoteTaxCode
      ? new TaxCode(salesCreditNoteTaxCode)
      : null;
    this.salesCreditNoteDueDate = salesCreditNoteDueDate
      ? new DueDate(salesCreditNoteDueDate)
      : null;
    this.purchaseInvoiceJournal = purchaseInvoiceJournal
      ? new Journal(purchaseInvoiceJournal)
      : null;
    this.purchaseInvoiceTaxCode = purchaseInvoiceTaxCode
      ? new TaxCode(purchaseInvoiceTaxCode)
      : null;
    this.purchaseInvoiceDueDate = purchaseInvoiceDueDate
      ? new DueDate(purchaseInvoiceDueDate)
      : null;
    this.purchaseDiscountPercentage = purchaseDiscountPercentage ?? null;
    this.purchaseDiscountDays = purchaseDiscountDays ?? null;
    this.purchaseCreditNoteJournal = purchaseCreditNoteJournal
      ? new Journal(purchaseCreditNoteJournal)
      : null;
    this.purchaseCreditNoteTaxCode = purchaseCreditNoteTaxCode
      ? new TaxCode(purchaseCreditNoteTaxCode)
      : null;
    this.purchaseCreditNoteDueDate = purchaseCreditNoteDueDate
      ? new DueDate(purchaseCreditNoteDueDate)
      : null;
    this.purchaseResponsible = purchaseResponsible
      ? new Staff(purchaseResponsible)
      : null;
    this.purchaseAutomaticValidation = purchaseAutomaticValidation;
    this.purchaseCostAccount = purchaseCostAccount;
    this.salesRevenueAccount = salesRevenueAccount;
    this.customData = customData;
    this.communicationModeData = communicationModeData;
    this.locale = locale || null;
  }

  static get baseModuleForRedirection() {
    return 'address-base';
  }
  get baseModuleForRedirection() {
    return Address.baseModuleForRedirection;
  }

  static get fkName() {
    return 'address_id';
  }
  static get apiRoute() {
    return 'address';
  }
  static get hasEntityStaffRolesBulkUpdate() {
    return true;
  }
  static get listPageRoute() {
    return 'addressList';
  }
  static get detailPageRoute() {
    return 'addressDetail';
  }
  static get printTemplateType() {
    return 'address';
  }
  get printTemplateType() {
    return Address.printTemplateType;
  }
  get fkName() {
    return Address.fkName;
  }
  get apiRoute() {
    return Address.apiRoute;
  }
  get listPageRoute() {
    return Address.listPageRoute;
  }
  get detailPageRoute() {
    return Address.detailPageRoute;
  }

  static get defaultSort() {
    return {
      property: 'number',
      order: 'asc',
    };
  }

  getDefaultSort() {
    return Address.defaultSort;
  }

  get salesInvoicePrefComplete() {
    return this.salesInvoiceJournal && this.salesInvoiceDueDate;
  }
  get salesCreditNotePrefComplete() {
    return this.salesCreditNoteJournal && this.salesCreditNoteDueDate;
  }
  get purchaseInvoicePrefComplete() {
    return this.purchaseInvoiceJournal && this.purchaseInvoiceDueDate;
  }
  get purchaseCreditNotePrefComplete() {
    return this.purchaseCreditNoteJournal && this.purchaseCreditNoteDueDate;
  }
  get newTransactionPreferenceValues() {
    let journal = null;
    let dueDateConfig = null;
    if (this.salesInvoicePrefComplete) {
      journal = this.salesInvoiceJournal;
      dueDateConfig = this.salesInvoiceDueDate;
    } else if (this.purchaseInvoicePrefComplete) {
      journal = this.purchaseInvoiceJournal;
      dueDateConfig = this.purchaseInvoiceDueDate;
    } else if (this.salesCreditNotePrefComplete) {
      journal = this.salesCreditNoteJournal;
      dueDateConfig = this.salesCreditNoteDueDate;
    } else if (this.purchaseCreditNotePrefComplete) {
      journal = this.purchaseCreditNoteJournal;
      dueDateConfig = this.purchaseCreditNoteDueDate;
    }
    let dueDate = new Date();
    if (!journal)
      return {
        due_date: dueDate,
        address_id: this.id,
      };
    if (dueDateConfig?.endOfMonth) {
      dueDate.setMonth(dueDate.getMonth() + 1);
      dueDate.setDate(0);
    }
    dueDate.setDate(dueDate.getDate() + dueDateConfig?.days ?? 0);
    return {
      journal_id: journal.id,
      due_date: dueDate,
      address_id: this.id,
    };
  }

  /**
   * Default cash discount for an invoice. Sale and purchase are stored separately.
   * Credit notes do not use this.
   */
  cashDiscountDefaults(journalType) {
    if (journalType === 'PURCHASE') {
      return {
        percentage: this.purchaseDiscountPercentage ?? null,
        days: this.purchaseDiscountDays ?? null,
      };
    }
    return {
      percentage: this.saleDiscountPercentage ?? null,
      days: this.saleDiscountDays ?? null,
    };
  }

  /**
   * Sync address preference for journal / dueDate / taxCode.
   * kind: 'journal' | 'dueDate' | 'taxCode'
   * journalType: 'SALE' | 'PURCHASE'
   * transactionType: 'INVOICE' | 'CREDITNOTE'
   */
  extractBillingPreference(kind, journalType, transactionType) {
    const isCreditNote = transactionType === 'CREDITNOTE';
    const propertyMap = {
      journal: {
        SALE: isCreditNote ? 'salesCreditNoteJournal' : 'salesInvoiceJournal',
        PURCHASE: isCreditNote
          ? 'purchaseCreditNoteJournal'
          : 'purchaseInvoiceJournal',
      },
      dueDate: {
        SALE: isCreditNote ? 'salesCreditNoteDueDate' : 'salesInvoiceDueDate',
        PURCHASE: isCreditNote
          ? 'purchaseCreditNoteDueDate'
          : 'purchaseInvoiceDueDate',
      },
      taxCode: {
        SALE: isCreditNote ? 'salesCreditNoteTaxCode' : 'salesInvoiceTaxCode',
        PURCHASE: isCreditNote
          ? 'purchaseCreditNoteTaxCode'
          : 'purchaseInvoiceTaxCode',
      },
    };
    const property = propertyMap[kind]?.[journalType];
    return property ? this[property] || undefined : undefined;
  }

  static bookkeepingDefaultLabel(kind, journalType, transactionType) {
    const side = journalType === 'PURCHASE' ? 'purchase' : 'sale';
    const doc = transactionType === 'CREDITNOTE' ? 'credit-note' : 'invoice';
    const kindPart = {
      journal: 'journal',
      dueDate: 'due-date',
      taxCode: 'vat-code',
    }[kind];
    if (!kindPart || !journalType) {
      return null;
    }
    return `bookkeeping.default.${side}-${kindPart}-${doc}`;
  }

  /** Loads company bookkeeping default entity (UUID stored in config). */
  static async loadCompanyBillingDefault(kind, journalType, transactionType) {
    const label = Address.bookkeepingDefaultLabel(
      kind,
      journalType,
      transactionType,
    );
    if (!label) {
      return undefined;
    }
    const res = await DataService.read('config', {
      attributes: 'value',
      filters: {label: `eq.${label}`},
    });
    const id = res?.data?.[0]?.value;
    if (!id) {
      return undefined;
    }
    const Model = {journal: Journal, dueDate: DueDate, taxCode: TaxCode}[kind];
    if (!Model) {
      return undefined;
    }
    return (await Model.loadEntityFromId(id)) || undefined;
  }

  /**
   * Priority: address preference → company config → undefined
   */
  async resolveBillingDefault(kind, journalType, transactionType) {
    const fromAddress = this.extractBillingPreference(
      kind,
      journalType,
      transactionType,
    );
    if (fromAddress) {
      return fromAddress;
    }
    return Address.loadCompanyBillingDefault(
      kind,
      journalType,
      transactionType,
    );
  }

  /**
   * Same priority when address may be missing (company config only then).
   */
  static async resolveBillingDefault(
    address,
    kind,
    journalType,
    transactionType,
  ) {
    if (address) {
      return address.resolveBillingDefault(kind, journalType, transactionType);
    }
    return Address.loadCompanyBillingDefault(
      kind,
      journalType,
      transactionType,
    );
  }

  /**
   * Descriptors for address billing FK preferences (invoice + credit note).
   * column = DB column name used for PATCH.
   */
  static get billingPreferenceDescriptors() {
    return [
      {
        kind: 'journal',
        journalType: 'SALE',
        transactionType: 'INVOICE',
        prop: 'salesInvoiceJournal',
        column: 'sale_invoice_journal_id',
      },
      {
        kind: 'taxCode',
        journalType: 'SALE',
        transactionType: 'INVOICE',
        prop: 'salesInvoiceTaxCode',
        column: 'sale_invoice_tax_code_id',
      },
      {
        kind: 'dueDate',
        journalType: 'SALE',
        transactionType: 'INVOICE',
        prop: 'salesInvoiceDueDate',
        column: 'sale_invoice_due_date_id',
      },
      {
        kind: 'journal',
        journalType: 'SALE',
        transactionType: 'CREDITNOTE',
        prop: 'salesCreditNoteJournal',
        column: 'sale_creditnote_journal_id',
      },
      {
        kind: 'taxCode',
        journalType: 'SALE',
        transactionType: 'CREDITNOTE',
        prop: 'salesCreditNoteTaxCode',
        column: 'sale_creditnote_tax_code_id',
      },
      {
        kind: 'dueDate',
        journalType: 'SALE',
        transactionType: 'CREDITNOTE',
        prop: 'salesCreditNoteDueDate',
        column: 'sale_creditnote_due_date_id',
      },
      {
        kind: 'journal',
        journalType: 'PURCHASE',
        transactionType: 'INVOICE',
        prop: 'purchaseInvoiceJournal',
        column: 'purchase_invoice_journal_id',
      },
      {
        kind: 'taxCode',
        journalType: 'PURCHASE',
        transactionType: 'INVOICE',
        prop: 'purchaseInvoiceTaxCode',
        column: 'purchase_invoice_tax_code_id',
      },
      {
        kind: 'dueDate',
        journalType: 'PURCHASE',
        transactionType: 'INVOICE',
        prop: 'purchaseInvoiceDueDate',
        column: 'purchase_invoice_due_date_id',
      },
      {
        kind: 'journal',
        journalType: 'PURCHASE',
        transactionType: 'CREDITNOTE',
        prop: 'purchaseCreditNoteJournal',
        column: 'purchase_creditnote_journal_id',
      },
      {
        kind: 'taxCode',
        journalType: 'PURCHASE',
        transactionType: 'CREDITNOTE',
        prop: 'purchaseCreditNoteTaxCode',
        column: 'purchase_creditnote_tax_code_id',
      },
      {
        kind: 'dueDate',
        journalType: 'PURCHASE',
        transactionType: 'CREDITNOTE',
        prop: 'purchaseCreditNoteDueDate',
        column: 'purchase_creditnote_due_date_id',
      },
    ];
  }

  /**
   * Builds a DB patch from company bookkeeping defaults for empty address prefs.
   * Does not overwrite existing address preferences.
   */
  static async buildCompanyBillingDefaultsPatch(
    address,
    {customer, supplier} = {},
  ) {
    const isCustomer = customer ?? address?.customer;
    const isSupplier = supplier ?? address?.supplier;
    if (!address || (!isCustomer && !isSupplier)) {
      return {dbPatch: {}, entityUpdates: {}};
    }

    const descriptors = Address.billingPreferenceDescriptors.filter((d) => {
      if (d.journalType === 'SALE') return !!isCustomer;
      if (d.journalType === 'PURCHASE') return !!isSupplier;
      return false;
    });

    const dbPatch = {};
    const entityUpdates = {};
    await Promise.all(
      descriptors.map(async (d) => {
        if (address[d.prop]) {
          return;
        }
        const entity = await Address.loadCompanyBillingDefault(
          d.kind,
          d.journalType,
          d.transactionType,
        );
        if (!entity?.id) {
          return;
        }
        dbPatch[d.column] = entity.id;
        entityUpdates[d.prop] = entity;
      }),
    );
    return {dbPatch, entityUpdates};
  }

  static get joinAttrsKey() {
    return [
      'number',
      'designation',
      'street',
      'houseNo',
      'postcode',
      'city',
      'country',
      'salesInvoiceJournal',
      'salesInvoiceTaxCode',
      'salesInvoiceDueDate',
      'salesCreditNoteJournal',
      'salesCreditNoteTaxCode',
      'salesCreditNoteDueDate',
      'purchaseInvoiceJournal',
      'purchaseInvoiceTaxCode',
      'purchaseInvoiceDueDate',
      'purchaseCreditNoteJournal',
      'purchaseCreditNoteTaxCode',
      'purchaseCreditNoteDueDate',
      'purchaseCostAccount',
      'salesRevenueAccount',
      'customer',
      'potentialCustomer',
      'supplier',
      'potentialSupplier',
      'locale',
    ];
  }

  static get listDisplayAttrs() {
    return this.getAttributes([
      'number',
      'designation',
      'street',
      'houseNo',
      'boxNo',
      'postcode',
      'city',
      'country',
      'customer',
      'potentialCustomer',
      'supplier',
      'potentialSupplier',
      'active',
      'tags',
      'creationDate',
      'modificationDate',
    ]);
  }

  static get detailAttrs() {
    return this.getAttributes([
      'number',
      'designation',
      'street',
      'houseNo',
      'boxNo',
      'postcode',
      'city',
      'country',
      'location',
      'calculatedLocation',
      'customer',
      'potentialCustomer',
      'supplier',
      'potentialSupplier',
      'active',
      'locale',
      'tags',
      'url',
      'memo',
      'customData',
      'salesInvoiceJournal',
      'purchaseInvoiceJournal',
      'salesInvoiceDueDate',
      'purchaseInvoiceDueDate',
    ]);
  }

  static get salesBillingConfigAttrs() {
    return this.getAttributes([
      'salesInvoiceJournal',
      'salesInvoiceTaxCode',
      'salesInvoiceDueDate',
      'saleDiscountPercentage',
      'saleDiscountDays',
      'salesCreditNoteJournal',
      'salesCreditNoteTaxCode',
      'salesCreditNoteDueDate',
      'salesRevenueAccount',
    ]);
  }

  static get purchaseBillingConfigAttrs() {
    return this.getAttributes([
      'purchaseInvoiceJournal',
      'purchaseInvoiceTaxCode',
      'purchaseInvoiceDueDate',
      'purchaseDiscountPercentage',
      'purchaseDiscountDays',
      'purchaseCreditNoteJournal',
      'purchaseCreditNoteTaxCode',
      'purchaseCreditNoteDueDate',
      'purchaseCostAccount',
      'purchaseResponsible',
      'purchaseAutomaticValidation',
    ]);
  }

  static get browserDisplayAttrs() {
    return this.getAttributes(
      [
        'number',
        'designation',
        'street',
        'houseNo',
        'postcode',
        'city',
        'country',
        'customer',
        'supplier',
        'tags',
      ],
      [
        'salesInvoiceJournal',
        'salesInvoiceTaxCode',
        'salesInvoiceDueDate',
        'saleDiscountPercentage',
        'saleDiscountDays',
        'salesCreditNoteJournal',
        'salesCreditNoteTaxCode',
        'salesCreditNoteDueDate',
        'purchaseInvoiceJournal',
        'purchaseInvoiceTaxCode',
        'purchaseInvoiceDueDate',
        'purchaseDiscountPercentage',
        'purchaseDiscountDays',
        'purchaseCreditNoteJournal',
        'purchaseCreditNoteTaxCode',
        'purchaseCreditNoteDueDate',
        'purchaseCostAccount',
        'salesRevenueAccount',
        'locale',
      ],
    );
  }

  static get bulkUpdateAttrs() {
    return {
      'hit-base.bulk-update.general-information': this.getAttributes([
        'number',
        'designation',
        'street',
        'houseNo',
        'boxNo',
        'postcode',
        'city',
        'country',
        'customer',
        'potentialCustomer',
        'supplier',
        'potentialSupplier',
        'active',
        'tags',
      ]),
      'hit-app.common.sales-billing-parameters': this.getAttributes([
        'salesInvoiceJournal',
        'salesInvoiceTaxCode',
        'salesInvoiceDueDate',
        'salesCreditNoteJournal',
        'salesCreditNoteTaxCode',
        'salesCreditNoteDueDate',
      ]),
      'hit-app.common.purchase-billing-parameters': this.getAttributes([
        'purchaseInvoiceJournal',
        'purchaseInvoiceTaxCode',
        'purchaseInvoiceDueDate',
        'purchaseCreditNoteJournal',
        'purchaseCreditNoteTaxCode',
        'purchaseCreditNoteDueDate',
      ]),
      ...this.entityStaffRolesBulkUpdateAttrs,
    };
  }

  static get allAttributes() {
    return [
      this.NUMBER_ATTRIBUTE(),
      this.DESIGNATION_ATTRIBUTE(),
      new AT.StringAttribute({
        name: 'street',
        tableProperties: {
          header: 'hit-app.common.street',
          maxWidth: '1fr',
        },
      }),
      new AT.StringAttribute({
        name: 'houseNo',
        tableProperties: {
          header: 'hit-app.common.house-number',
          maxWidth: '0.5fr',
          sharable: true,
        },
        column: 'house_no',
      }),
      new AT.StringAttribute({
        name: 'boxNo',
        tableProperties: {
          header: 'hit-app.common.box-number',
          maxWidth: '0.5fr',
          sharable: true,
          hideByDefault: true,
        },
        column: 'box_no',
      }),
      new AT.StringAttribute({
        name: 'postcode',
        tableProperties: {
          header: 'hit-app.common.postcode',
          maxWidth: '0.5fr',
        },
      }),
      new AT.StringAttribute({
        name: 'city',
        tableProperties: {
          header: 'hit-app.common.city',
          maxWidth: '1fr',
        },
      }),
      new AT.FkCountryAttribute({
        name: 'country',
        joinPath: ['country!fk_address_country_country'],
        tableProperties: {
          header: 'hit-base.common.country',
          maxWidth: '1fr',
        },
      }),
      new AT.LocationAttribute({
        name: 'location',
        tableProperties: {
          header: 'hit-base.common.location',
          maxWidth: '1fr',
        },
      }),
      new AT.BooleanAttribute({
        name: 'customer',
        tableProperties: {
          propertyName: 'customer',
          header: 'hit-app.common.customer',
          maxWidth: '0.5fr',
        },
      }),
      new AT.BooleanAttribute({
        name: 'potentialCustomer',
        column: 'potential_customer',
        tableProperties: {
          header: 'hit-app.address.potential-customer',
          maxWidth: '0.5fr',
          hideByDefault: true,
        },
      }),
      new AT.BooleanAttribute({
        name: 'supplier',
        tableProperties: {
          propertyName: 'supplier',
          header: 'hit-app.common.supplier',
          maxWidth: '0.5fr',
        },
      }),
      new AT.BooleanAttribute({
        name: 'potentialSupplier',
        column: 'potential_supplier',
        tableProperties: {
          propertyName: 'potentialSupplier',
          header: 'hit-app.address.potential-supplier',
          maxWidth: '0.5fr',
          hideByDefault: true,
        },
      }),
      this.ACTIVE_ATTRIBUTE(),
      new AT.LanguageAttribute({
        name: 'locale',
        tableProperties: {
          header: 'hit-base.common.language',
          maxWidth: '1fr',
        },
      }),
      this.TAGS_ATTRIBUTE({moduleUsage: 'address', tableOrigin: 'address'}),
      new AT.StringAttribute({
        name: 'url',
        validations: {url},
        tableProperties: {
          header: 'hit-app.common.url',
          maxWidth: '1fr',
        },
      }),
      this.MEMO_RICH_TEXT_ATTRIBUTE(),
      new AT.FkJournalAttribute({
        name: 'purchaseInvoiceJournal',
        joinPath: ['journal!fk_address_purchase_invoice_journal_id_journal'],
        fkToUpdate: 'purchase_invoice_journal_id',
        tableProperties: {
          header: 'hit-app.common.invoice-journal',
        },
        filters: {
          and: ['journal_type.eq.PURCHASE', 'transaction_type.eq.INVOICE'],
        },
      }),
      new AT.FkTaxCodeAttribute({
        name: 'purchaseInvoiceTaxCode',
        joinPath: ['tax_code!fk_address_purchase_invoice_tax_code_id_tax_code'],
        fkToUpdate: 'purchase_invoice_tax_code_id',
        tableProperties: {
          header: 'hit-app.common.invoice-tax-code',
        },
        filters: {journal_type: 'eq.PURCHASE'},
      }),
      new AT.FkDueDateAttribute({
        name: 'purchaseInvoiceDueDate',
        joinPath: ['due_date!fk_address_purchase_invoice_due_date_id_due_date'],
        fkToUpdate: 'purchase_invoice_due_date_id',
        tableProperties: {
          header: 'hit-app.common.invoice-due-date',
        },
      }),
      new AT.DecimalAttribute({
        name: 'purchaseDiscountPercentage',
        column: 'purchase_discount_percentage',
        decimalPrecision: 2,
        decimalKeepNullValue: true,
        tableProperties: {
          header: 'hit-app.common.cash-discount-percentage',
        },
      }),
      new AT.IntegerAttribute({
        name: 'purchaseDiscountDays',
        column: 'purchase_discount_days',
        tableProperties: {
          header: 'hit-app.common.cash-discount-days',
        },
      }),
      new AT.FkJournalAttribute({
        name: 'purchaseCreditNoteJournal',
        joinPath: ['journal!fk_address_purchase_creditnote_journal_id_journal'],
        fkToUpdate: 'purchase_creditnote_journal_id',
        tableProperties: {
          header: 'hit-app.common.credit-note-journal',
        },
        filters: {
          and: ['journal_type.eq.PURCHASE', 'transaction_type.eq.CREDITNOTE'],
        },
      }),
      new AT.FkTaxCodeAttribute({
        name: 'purchaseCreditNoteTaxCode',
        joinPath: [
          'tax_code!fk_address_purchase_creditnote_tax_code_id_tax_code',
        ],
        fkToUpdate: 'purchase_creditnote_tax_code_id',
        tableProperties: {
          header: 'hit-app.common.credit-note-tax-code',
        },
        filters: {journal_type: 'eq.PURCHASE'},
      }),
      new AT.FkDueDateAttribute({
        name: 'purchaseCreditNoteDueDate',
        joinPath: [
          'due_date!fk_address_purchase_creditnote_due_date_id_due_date',
        ],
        fkToUpdate: 'purchase_creditnote_due_date_id',
        tableProperties: {
          header: 'hit-app.common.credit-note-due-date',
        },
      }),

      new AT.FkAccountAttribute({
        name: 'purchaseCostAccount',
        joinPath: ['account!fk_address_cost_account_id_account'],
        fkToUpdate: 'cost_account_id',
        tableProperties: {
          header: 'hit-app.common.cost-account',
        },
        filters: {account_type: 'eq.COST'},
      }),
      new AT.FkAccountAttribute({
        name: 'salesRevenueAccount',
        joinPath: ['account!fk_address_revenue_account_id_account'],
        fkToUpdate: 'revenue_account_id',
        tableProperties: {
          header: 'hit-app.common.revenue-account',
        },
        filters: {account_type: 'eq.REVENUE'},
      }),
      new AT.FkStaffAttribute({
        name: 'purchaseResponsible',
        joinPath: ['staff!fk_address_purchase_responsible_id_staff'],
        fkToUpdate: 'purchase_responsible_id',
        tableProperties: {
          header: 'hit-app.common.standard-responsible',
        },
        filters: {user_id: 'not.is.null'},
      }),
      new AT.BooleanAttribute({
        name: 'purchaseAutomaticValidation',
        column: 'purchase_automatic_validation',
        tableProperties: {
          header: 'hit-app.transaction.purchase-automatic-validation',
        },
      }),
      new AT.FkJournalAttribute({
        name: 'salesInvoiceJournal',
        joinPath: ['journal!fk_address_sale_invoice_journal_id_journal'],
        fkToUpdate: 'sale_invoice_journal_id',
        tableProperties: {
          header: 'hit-app.common.invoice-journal',
        },
        filters: {
          and: ['journal_type.eq.SALE', 'transaction_type.eq.INVOICE'],
        },
      }),
      new AT.FkTaxCodeAttribute({
        name: 'salesInvoiceTaxCode',
        joinPath: ['tax_code!fk_address_sale_invoice_tax_code_id_tax_code'],
        fkToUpdate: 'sale_invoice_tax_code_id',
        tableProperties: {
          header: 'hit-app.common.invoice-tax-code',
        },
        filters: {journal_type: 'eq.SALE'},
      }),
      new AT.FkDueDateAttribute({
        name: 'salesInvoiceDueDate',
        joinPath: ['due_date!fk_address_sale_invoice_due_date_id_due_date'],
        fkToUpdate: 'sale_invoice_due_date_id',
        tableProperties: {
          header: 'hit-app.common.invoice-due-date',
        },
      }),
      new AT.DecimalAttribute({
        name: 'saleDiscountPercentage',
        column: 'sale_discount_percentage',
        decimalPrecision: 2,
        decimalKeepNullValue: true,
        tableProperties: {
          header: 'hit-app.common.cash-discount-percentage',
        },
      }),
      new AT.IntegerAttribute({
        name: 'saleDiscountDays',
        column: 'sale_discount_days',
        tableProperties: {
          header: 'hit-app.common.cash-discount-days',
        },
      }),
      new AT.FkJournalAttribute({
        name: 'salesCreditNoteJournal',
        joinPath: ['journal!fk_address_sale_creditnote_journal_id_journal'],
        fkToUpdate: 'sale_creditnote_journal_id',
        tableProperties: {
          header: 'hit-app.common.credit-note-journal',
        },
        filters: {
          and: ['journal_type.eq.SALE', 'transaction_type.eq.CREDITNOTE'],
        },
      }),
      new AT.FkTaxCodeAttribute({
        name: 'salesCreditNoteTaxCode',
        joinPath: ['tax_code!fk_address_sale_creditnote_tax_code_id_tax_code'],
        fkToUpdate: 'sale_creditnote_tax_code_id',
        tableProperties: {
          header: 'hit-app.common.credit-note-tax-code',
        },
        filters: {journal_type: 'eq.SALE'},
      }),
      new AT.FkDueDateAttribute({
        name: 'salesCreditNoteDueDate',
        joinPath: ['due_date!fk_address_sale_creditnote_due_date_id_due_date'],
        fkToUpdate: 'sale_creditnote_due_date_id',
        tableProperties: {
          header: 'hit-app.common.credit-note-due-date',
        },
      }),
      new AT.BooleanAttribute({
        name: 'calculatedLocation',
        column: 'calculated_location',
      }),
      new AT.JsonObjectAttribute({
        name: 'customData',
        column: 'custom_data',
      }),
      new AT.FkAddressCommunicationModeAttribute({
        name: 'communicationModeData',
        joinPath: [
          'address_communication_mode_data!fk_address_communication_mode_data_address_id_address',
        ],
      }),
      new AT.DateAttribute({
        name: 'creationDate',
        column: 'creation_date',
        tableProperties: {
          header: 'hit-base.common.creation-date',
          hideByDefault: true,
        },
      }),
      new AT.DateAttribute({
        name: 'modificationDate',
        column: 'modification_date',
        tableProperties: {
          header: 'hit-base.common.last-modified',
          hideByDefault: true,
        },
      }),
    ];
  }

  /**
   * For the address entity, we need to check if it nos the address of the owning company
   * This address can not be deleted because it is used for the transaction
   */
  async delete(router) {
    await DataService.read('config', {
      attributes: 'value',
      filters: {label: 'eq.company.address.id'},
    }).then(async (response) => {
      if (response.data.length > 0) {
        const companyAddressId = response.data[0].value;
        if (companyAddressId === this.id) {
          useNotificationsStore().insertBannerNotification(
            translate('hit-base.banners.cannot-delete-main-company-address'),
          );
          return false;
        }
      }
      return super.delete(router);
    });
  }

  /** Returns all the registered mail addresses for the address */
  get mailAddresses() {
    if (!this.communicationModeData) return [];
    return this.communicationModeData
      .filter((c) => c.mode === CommunicationMode.ID_EMAIL)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  /** Depending on the journal and transaction type, we take different tax code preferences */
  extractTaxCodePreference(journalType, transactionType) {
    return (
      this.extractBillingPreference('taxCode', journalType, transactionType) ??
      null
    );
  }

  /** Loads all the available mail addresses for the given addressId */
  static async loadMailAddresses(addressId) {
    const res = await DataService.read('address_communication_mode_data', {
      attributes: 'id, value, communication_mode_id',
      filters: {
        address_id: `eq.${addressId}`,
        communication_mode_id: `eq.${CommunicationMode.ID_EMAIL}`,
      },
    });
    return res.data ?? [];
  }

  /** Loads mail addresses of contacts linked to the given address */
  static async loadContactMailAddresses(addressId) {
    const res = await DataService.read('address_contact', {
      attributes:
        'contact(mails:contact_communication_mode_data(communication_mode_id, value, id))',
      filters: {
        address_id: `eq.${addressId}`,
        'contact.contact_communication_mode_data.communication_mode_id': `eq.${CommunicationMode.ID_EMAIL}`,
      },
    });
    let mails = [];
    (res.data ?? []).forEach((item) => {
      mails = mails.concat(item.contact?.mails ?? []);
    });
    return mails;
  }

  /**
   * Loads address + contact mail addresses for the given addressId,
   * deduplicated by value.
   */
  static async loadAllMailAddresses(addressId) {
    if (!addressId) {
      return [];
    }
    const [addressMails, contactMails] = await Promise.all([
      Address.loadMailAddresses(addressId),
      Address.loadContactMailAddresses(addressId),
    ]);
    return addressMails
      .concat(contactMails)
      .filter(
        (mail, index, self) =>
          index === self.findIndex((m) => m.value === mail.value),
      );
  }
}
